import { ALLOW_MOTIVATION_EVENT_LIST } from '@/helpers/constants'
import { dateProvider } from '@/helpers/date'
import { getAgeDecade, getAgeDecadeNumber } from '@/helpers/utils'
import { calcSafeWeightLossDelta } from '@/helpers/weightPredictor'

/**
 * Attaches a mandatory `description` string to a thunk. Runtime consumers keep
 * calling `thunk()` as if it were a bare `() => string`; the manifest generator
 * reads `thunk.description` to ship documentation in
 * `dist/runtime-manifest.json` without forcing a second source of truth.
 *
 * Descriptions must be short (target ≤ 30 words, ideally one sentence).
 * Prefer `Example: "<render>". <short context>.` when a representative render
 * is obvious. No translation-key or live-render noise — the thunk body is
 * never invoked at manifest time.
 *
 * @template {(...args: never[]) => unknown} F
 * @param {string} description - Non-empty English one-liner. Throws otherwise.
 * @param {F} resolve
 * @returns {F & { description: string }}
 */
function describe(description, resolve) {
	if (typeof description !== 'string' || description.trim().length === 0) {
		throw new Error('text-interpolation: description must be a non-empty string')
	}
	if (typeof resolve !== 'function') {
		throw new Error('text-interpolation: resolve must be a function')
	}
	resolve.description = description
	return resolve
}

export default function textInterpolations(model, $t) {
	const ctx = {
		...model,
		$t,
	}

	return {
		terms_of_use:
			describe('Example: "[Terms of Use](…)". Language-aware Terms of Use markdown link for the signup agreement disclaimer.', () =>
				`[${ctx.$t('pages.signup.terms_of_use')}](${ctx.legalLinks?.tos ?? ''} "_blank")`),
		privacy_policy:
			describe('Example: "[Privacy Policy](…)". Language-aware Privacy Policy markdown link for the signup agreement disclaimer.', () =>
				`[${ctx.$t('pages.signup.privacy_policy')}](${ctx.legalLinks?.privacy ?? ''} "_blank")`),
		privacy_policy_dark:
			describe('Example: "<a …>Privacy Policy</a>". Privacy Policy link forced to primary text color, for checkbox consent where the link must match body text (e.g. h-input health-data consent).', () =>
				`<a href="${ctx.legalLinks?.privacy ?? ''}" target="_blank" rel="noopener noreferrer" style="color:var(--text-dark-primary);text-decoration:underline">${ctx.$t('pages.signup.privacy_policy')}</a>`),
		userBmi:
			describe('Example: "23.4". The user\'s BMI rounded to one decimal; empty until height and weight are both set. Used by the w-input healthy-range card.', () =>
				typeof ctx.bmiValue === 'number' ? String(Math.round(ctx.bmiValue * 10) / 10) : ''),
		userBmiRounded:
			describe('Example: "23". The user\'s BMI rounded to a whole number; empty until height and weight are both set. Used by the bmi-report card header value.', () =>
				typeof ctx.bmiValue === 'number' ? String(Math.round(ctx.bmiValue)) : ''),
		userBmiCategory:
			describe('Example: "normal". The user\'s BMI category word (underweight / normal / overweight / obesity); empty until BMI is known. Pairs with `userBmi` in the w-input card.', () =>
				String(ctx.bmiCategory || '')),
		weightLossGoalsFirstOption: describe('Example: "1-10 kg". Small-loss range label, unit-system aware.', () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `1-20 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `1-10 ${ctx.$t('common.kg')}`
				case 'st':
					return `1-1,5 ${ctx.$t('common.st')}`
				default:
					return `1-10 ${ctx.$t('common.kg')}`
			}
		}),
		weightLossGoalsSecondOption: describe('Example: "11-20 kg". Medium-loss range label, unit-system aware.', () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `21-50 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `11-20 ${ctx.$t('common.kg')}`
				case 'st':
					return `2-3 ${ctx.$t('common.st')}`
				default:
					return `11-20 ${ctx.$t('common.kg')}`
			}
		}),
		weightLossGoalsThirdOption: describe('Example: "20 kg". Large-loss range label, open-ended upper bound.', () => {
			switch (ctx.weightUnitSystem) {
				case 'lbs':
					return `50 ${ctx.$t('common.lbs')}`
				case 'kg':
					return `20 ${ctx.$t('common.kg')}`
				case 'st':
					return `4+ ${ctx.$t('common.st')}`
				default:
					return `20 ${ctx.$t('common.kg')}`
			}
		}),
		motivationalEventTitle:
			describe('Localized title of the user-picked motivation event (wedding, vacation, reunion, …); falls back to the "other" copy.', () => {
				const sourceProperty = ctx.motivation_event?.[0] || null
				const eventList = ALLOW_MOTIVATION_EVENT_LIST
				const eventIsValid = sourceProperty && eventList.includes(sourceProperty)
				const event = eventIsValid ? sourceProperty : 'other'

				if (event === 'baby_weight') {
					return ctx.$t('pages.when_is_the_event.title.baby_weight')
				}

				if (event === 'school') {
					return ctx.$t('pages.when_is_the_event.title.reunion')
				}

				return ctx.$t(`pages.when_is_the_event.title.${event}`)
			}),
		motivationalEventTitleEventPersonalization:
			describe('Like `motivationalEventTitle`, but "wedding" routes to the v2 copy (event-personalization experiment variant).', () => {
				const sourceProperty = ctx.motivation_event?.[0] || null
				const eventList = ALLOW_MOTIVATION_EVENT_LIST
				const eventIsValid = sourceProperty && eventList.includes(sourceProperty)
				const event = eventIsValid ? sourceProperty : 'other'

				if (event === 'baby_weight') {
					return ctx.$t('pages.when_is_the_event.title.baby_weight')
				}

				if (event === 'school') {
					return ctx.$t('pages.when_is_the_event.title.reunion')
				}

				if (event === 'wedding') {
					return ctx.$t('pages.when_is_the_event.title.wedding_v2')
				}

				return ctx.$t(`pages.when_is_the_event.title.${event}`)
			}),
		gender: describe('Example: "women". Lowercase gender noun used inside sentences.', () =>
			(ctx.sex === 'male' ? ctx.$t('common.men') : ctx.$t('common.women')).toLowerCase()),
		genderUppercase: describe('Example: "Women". Title-case gender noun for sentence starts and headings.', () =>
			ctx.sex === 'male' ? ctx.$t('common.men') : ctx.$t('common.women')),
		genderNeutralUppercase:
			describe('Title-case gender noun; returns "People" when `non_binary_identity === "non_binary"`.', () => {
				return ctx.non_binary_identity !== 'non_binary'
					? ctx.sex === 'male'
						? ctx.$t('common.men')
						: ctx.$t('common.women')
					: ctx.$t('common.people')
			}),
		usersCountByGenderTitle:
			describe('Processing-screen title like "Women in their thirties". Requires `this.$t` — must be called on a Vue instance.', function () {
				return ctx.sex === 'male'
					? ctx.$t('pages.processing.male_title', {
							age: this.$t(`pages.processing.age_${getAgeDecadeNumber(ctx.age)}`),
						})
					: ctx.$t('pages.processing.female_title', {
							age: this.$t(`pages.processing.age_${getAgeDecadeNumber(ctx.age)}`),
						})
			}),
		ageDecade: describe('Age-decade phrase (twenties, thirties, …) for additional-question-1 copy.', () =>
			ctx.$t(`pages.additional-question-1.age.${getAgeDecade(ctx.age)}`)),
		constitution: describe('Example: "your". Possessive pronoun prefix for body-constitution sentences.', () =>
			ctx.constitution ?? 'your'),
		ageDecadeWords: describe('Age-decade phrase for additional-question-2 copy (second variant).', () =>
			ctx.$t(`pages.additional-question-2.age.${getAgeDecade(ctx.age)}`)),
		ageNumber: describe('Example: "35". User age as a plain number string.', () =>
			String(Array.isArray(ctx.age) ? ctx.age[0] : ctx.age)),
		ageNumberPlus: describe('Example: "35+". User age with a trailing plus sign.', () => `${ctx.age}+`),
		userName: describe('Example: "Alex". User first name; empty string when not set.', () =>
			String(ctx.userName || '')),
		dreamBodyText: describe('Example: "slim and toned". Target-body adjective phrase; empty string when not set.', () =>
			String(ctx.dreamBodyText || '')),
		targetWeightRange:
			describe('Example: "55-75 kg". Target-weight range (±10 kg / ±20 lbs / ±1 st), unit-system aware.', () => {
				const weight = ctx.targetWeight
				let modifier = 1

				if (ctx.weightUnitSystem === 'kg') {
					modifier = 10
				}
				if (ctx.weightUnitSystem === 'lbs') {
					modifier = 20
				}

				return `${weight - modifier + '-' + (weight + modifier)} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`
			}),
		targetWeight: describe('Example: "65 kg". User\'s target weight with unit suffix.', () =>
			`${ctx.targetWeight} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`),
		currentWeight:
			describe('Example: "80 kg". User\'s current weight with unit suffix; mirrors targetWeight for the start-of-chart label.', () =>
				`${ctx.currentWeight} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`),
		goalLabel:
			describe('Example: "Goal". Localised noun used as the prefix line of the weight-loss chart goal pill.', () =>
				ctx.$t('pages.wls_chart.goal')),
		/**
		 * @deprecated
		 * Instead use prediction date with steps to show appropriate predict date explicitly.
		 * this function is now hardcoded to show step 2 predict
		 * @returns {string}
		 */
		predictionDate:
			describe('Deprecated — long-form step-2 prediction date. Use `predictionDate_1..4` instead.', () => {
				if (!ctx.dateEnd) {
					return ''
				}
				return dateProvider.toLongDate(new Date(ctx.dateEnd))
			}),
		predictionDate_1:
			describe('Example: "May 1, 2026". Long-form date, prediction milestone 1 (earliest, ~2 weeks out).', () => {
				if (!ctx.weightPredictDate_1) {
					return ''
				}
				return dateProvider.toLongDate(new Date(ctx.weightPredictDate_1))
			}),
		predictionDate_2:
			describe('Example: "May 15, 2026". Long-form date, prediction milestone 2 (~1 month out).', () => {
				if (!ctx.weightPredictDate_2) {
					return ''
				}
				return dateProvider.toLongDate(new Date(ctx.weightPredictDate_2))
			}),
		predictionDate_3:
			describe('Example: "June 1, 2026". Long-form date, prediction milestone 3 (~6 weeks out).', () => {
				if (!ctx.weightPredictDate_3) {
					return ''
				}
				return dateProvider.toLongDate(new Date(ctx.weightPredictDate_3))
			}),
		predictionDate_4: describe('Example: "July 1, 2026". Long-form date, prediction milestone 4 (goal end).', () => {
			if (!ctx.weightPredictDate_4) {
				return ''
			}
			return dateProvider.toLongDate(new Date(ctx.weightPredictDate_4))
		}),
		predictionDate_4_short: describe('Example: "July 1". Short (month + day) goal-end date for compact chips.', () => {
			if (!ctx.weightPredictDate_4) {
				return ''
			}
			return dateProvider.toFullMonthDay(new Date(ctx.weightPredictDate_4))
		}),
		predictionDate_4_short_with_year:
			describe('Example: "Aug 2026". Short month + year goal-end date — used in the predict subtitle ("Reach X by …").', () => {
				if (!ctx.weightPredictDate_4) {
					return ''
				}
				return dateProvider.toShortMonthWithYear(new Date(ctx.weightPredictDate_4))
			}),
		predictionDate_4_month:
			describe('Example: "Aug". Short month (no year) goal-end date — used in the chart end-axis label.', () => {
				if (!ctx.weightPredictDate_4) {
					return ''
				}
				return dateProvider.toShortMonth(new Date(ctx.weightPredictDate_4))
			}),
		weightDelta:
			describe('Example: "10 kg". `currentWeight − targetWeight` with unit suffix; empty when either is missing.', () => {
				if (!ctx.weight || !ctx.targetWeight) {
					return ''
				}
				return `${ctx.currentWeight - ctx.targetWeight} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`
			}),
		safeWeightLoss:
			describe('Example: "3 kg". Safe loss capped at 5% of current weight; empty below the 1-unit floor.', () => {
				if (!ctx.currentWeight) {
					return ''
				}
				// Use `currentWeight` (already in the display unit) — NOT the raw
				// `weight.value` (always kg). The result is suffixed with
				// `weightUnitSystem`, so a kg-based delta would be mislabeled lbs/st
				// (e.g. "4 lbs" instead of "9 lbs") and contradict the chart's
				// own display-unit "decrease risk" marker on the same screen.
				const minSafeWeightLoss = calcSafeWeightLossDelta(ctx.currentWeight)
				const MIN_VALUE = 1

				if (minSafeWeightLoss > MIN_VALUE) {
					switch (ctx.weightUnitSystem) {
						case 'lbs':
							return `${minSafeWeightLoss} ${ctx.$t('common.lbs')}`
						case 'kg':
							return `${minSafeWeightLoss} ${ctx.$t('common.kg')}`
						case 'st':
							return `${minSafeWeightLoss} ${ctx.$t('common.st')}`
						default:
							return `${minSafeWeightLoss} ${ctx.$t('common.kg')}`
					}
				}

				return ''
			}),
		decreaseRiskWeight:
			describe('Example: "152 lbs". Current weight minus the safe 5% loss — the weight at the chart\'s "decrease health risk" marker.', () => {
				if (!ctx.currentWeight) {
					return ''
				}
				return `${ctx.currentWeight - calcSafeWeightLossDelta(ctx.currentWeight)} ${ctx.$t(`common.${ctx.weightUnitSystem}`)}`
			}),
		goalPersonalizationString:
			describe('Goal-specific sentence fragment (lose_weight / be_healthy / get_active / other).', () => {
				const goal = ctx.main_goal[0]

				if (goal === 'lose_weight') {
					return ctx.$t('common.goal_personalization.lose_weight')
				}
				if (goal === 'be_healthy') {
					return ctx.$t('common.goal_personalization.be_healthy')
				}
				if (goal === 'get_active') {
					return ctx.$t('common.goal_personalization.get_active')
				}

				return ctx.$t('common.goal_personalization.other')
			}),

		workoutIntensity:
			describe('Example: "Moderate-intensity workout". Label derived from `workout_level` (easy / medium / hard).', () => {
				const workoutLevel = ctx.workout_level[0]

				if (workoutLevel === 'easy') {
					return 'Low-intensity workout'
				}

				if (workoutLevel === 'hard') {
					return 'High-intensity workout'
				}

				// By default, we return the medium level
				return 'Moderate-intensity workout'
			}),

		targetBodyZones:
			describe('Example: "Belly, legs". Selected focus zones joined for the wellness-review row — first capitalized, rest lowercased.', () => {
				const zones = ctx.target_zones || ctx.body_focus_areas || ctx.body_target_zone
				if (!Array.isArray(zones) || zones.length === 0) {
					return ''
				}
				const zonesMap = {
					full_body: ctx.$t('female_pilates.wellness_review.value.target_zones.full'),
					booty: ctx.$t('female_pilates.wellness_review.value.target_zones.booty'),
					legs: ctx.$t('female_pilates.wellness_review.value.target_zones.legs'),
					belly: ctx.$t('female_pilates.wellness_review.value.target_zones.belly'),
					stomach: ctx.$t('female_pilates.wellness_review.value.target_zones.belly'),
					arms: ctx.$t('female_pilates.wellness_review.value.target_zones.arms'),
					butt: ctx.$t('female_pilates.wellness_review.value.target_zones.butt'),
					face_and_neck: ctx.$t('female_pilates.wellness_review.value.target_zones.face_and_neck'),
					glutes: ctx.$t('female_pilates.wellness_review.value.target_zones.glutes'),
					chest: ctx.$t('female_pilates.wellness_review.value.target_zones.chest'),
					back: ctx.$t('female_pilates.wellness_review.value.target_zones.back'),
					whole_body: ctx.$t('female_pilates.wellness_review.value.target_zones.whole_body'),
				}
				// "whole_body" supersedes every other selection.
				if (zones.includes('whole_body')) {
					return zonesMap.whole_body
				}
				// WALKING_FEMALE: keep the first zone capitalized, lowercase the rest.
				return zones.reduce((acc, zone) => {
					const mapped = zonesMap[zone]
					if (!mapped) {
						return acc
					}
					return acc === '' ? mapped : `${acc}, ${mapped.toLowerCase()}`
				}, '')
			}),
		addedPrograms:
			describe('Example: "Calisthenics, Tai Chi Walking". Comma-joined labels of the extra programs the user added on the add-more-plans screen; empty when none.', () => {
				const selected = ctx.additional_programs
				if (!Array.isArray(selected) || selected.length === 0) {
					return ''
				}
				const labels = {
					pilates: 'Pilates',
					calisthenics: 'Calisthenics',
					tai_chi_walking: 'Tai Chi Walking',
					yoga: 'Yoga',
					walking: 'Walking',
					fasting: 'Fasting',
				}
				return selected
					.map((value) => labels[value])
					.filter(Boolean)
					.join(', ')
			}),
	}
}
